import { useEffect, useState } from "react";

const TOC_GROUPS = [
  { label: "Foundation", links: [
    { id: "executive-summary", label: "Executive Summary" },
    { id: "vision-mission", label: "Vision & Mission" },
    { id: "problem", label: "The Problem" },
    { id: "osteon-solution", label: "OSTEON Solution" },
  ]},
  { label: "Anatomy", links: [
    { id: "scientific-foundation", label: "Scientific Foundation" },
    { id: "collection-architecture", label: "Collection Architecture" },
  ]},
  { label: "Collection", links: [
    { id: "ten-worlds", label: "Ten Worlds" },
    { id: "art-creation", label: "Art Creation" },
    { id: "metadata", label: "Metadata" },
    { id: "rarity-system", label: "Rarity System" },
  ]},
  { label: "Infrastructure", links: [
    { id: "blockchain", label: "Blockchain" },
    { id: "storage", label: "Storage" },
    { id: "mint-structure", label: "Mint Structure" },
  ]},
  { label: "Economics", links: [
    { id: "revenue-allocation", label: "Revenue & Funds" },
    { id: "royalties", label: "Royalties" },
    { id: "ip-rights", label: "IP Rights" },
  ]},
  { label: "Ecosystem", links: [
    { id: "utility", label: "Utility" },
    { id: "community", label: "Community" },
    { id: "team", label: "Team & Advisors" },
    { id: "roadmap", label: "Roadmap" },
    { id: "governance", label: "Governance" },
  ]},
  { label: "Trust", links: [
    { id: "security", label: "Security" },
    { id: "legal", label: "Legal" },
    { id: "risks", label: "Risks" },
    { id: "sustainability", label: "Sustainability" },
    { id: "success-metrics", label: "Success Metrics" },
  ]},
  { label: "Reference", links: [
    { id: "faq", label: "FAQ" },
    { id: "glossary", label: "Glossary" },
  ]},
];

function useActiveSection() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const ids = ["disclaimer", ...TOC_GROUPS.flatMap((g) => g.links.map((l) => l.id))];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return active;
}

function TocLinks({ active, onNavigate }) {
  return (
    <nav aria-label="Whitepaper table of contents" className="space-y-6">
      <div>
        <a
          href="#disclaimer"
          onClick={onNavigate}
          className={`block font-mono-cyber text-[11px] uppercase tracking-widest py-1 border-l pl-3 transition-colors ${
            active === "disclaimer"
              ? "text-[#ffb703] border-[#ffb703]"
              : "text-slate-400 border-[#1b2234] hover:text-[#ffb703]"
          }`}
        >
          Important Disclaimer
        </a>
      </div>
      {TOC_GROUPS.map((group) => (
        <div key={group.label}>
          <div className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-[#00f0ff] mb-2">
            {group.label}
          </div>
          <div className="space-y-0.5">
            {group.links.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={onNavigate}
                aria-current={active === link.id ? "true" : undefined}
                className={`block font-mono-cyber text-[11px] uppercase tracking-widest py-1 border-l pl-3 transition-colors ${
                  active === link.id
                    ? "text-[#00f0ff] border-[#00f0ff]"
                    : "text-slate-400 border-[#1b2234] hover:text-slate-100"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function Section({ id, index, title, children }) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-28 border-t border-[#1b2234] pt-12 first:border-t-0 first:pt-0">
      <div className="font-mono-cyber text-[11px] uppercase tracking-[0.35em] text-[#00f0ff] mb-4">
        {index}
      </div>
      <h2 id={`${id}-heading`} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight leading-tight">
        {title}
      </h2>
      <div className="mt-6 space-y-5 text-slate-300 text-base leading-[1.85] max-w-3xl">
        {children}
      </div>
    </section>
  );
}

function Sub({ children }) {
  return (
    <h3 className="font-heading text-lg sm:text-xl font-bold uppercase tracking-wide text-white !mt-8">
      {children}
    </h3>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <span aria-hidden="true" className="text-[#00f0ff] flex-shrink-0">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Table({ headers, rows, caption }) {
  return (
    <div className="overflow-x-auto -mx-1 px-1">
      <table className="w-full min-w-[480px] border border-[#1b2234] bg-[#0d1017]/70 text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-[#1b2234]">
            {headers.map((h) => (
              <th key={h} scope="col" className="px-5 py-3 text-left font-mono-cyber text-[10px] uppercase tracking-[0.25em] text-[#00f0ff] font-normal">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.join("|")} className={`border-b border-[#1b2234]/60 last:border-0 ${i % 2 === 1 ? "bg-white/[0.02]" : ""}`}>
              {row.map((cell, j) => (
                <td key={`${i}-${j}`} className={`px-5 py-3 ${j === 0 ? "text-slate-100 font-medium" : "text-slate-400 font-mono-cyber text-[13px]"}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Formula({ children }) {
  return (
    <div className="cyber-corners border border-[#00f0ff]/30 bg-[#00f0ff]/5 clip-terminal px-6 py-5 text-center">
      <span className="font-heading text-xl sm:text-2xl font-bold text-[#00f0ff]">{children}</span>
    </div>
  );
}

const WORLDS = [
  { n: "9.1", name: "Origin", desc: "Natural organic bone; ivory and earth tones; authenticity, history, and biological foundation." },
  { n: "9.2", name: "Obsidian", desc: "Volcanic black material with reflective edges; protection, endurance, and mystery." },
  { n: "9.3", name: "Gold", desc: "Polished or ancient gold; value, permanence, and legacy." },
  { n: "9.4", name: "Crystal", desc: "Transparent refractive material; clarity, precision, and transformation." },
  { n: "9.5", name: "Cyber", desc: "Metal, circuitry, and neon accents; technology, augmentation, and evolution." },
  { n: "9.6", name: "Bio", desc: "Living regenerative material and bioluminescence; growth, adaptation, and renewal." },
  { n: "9.7", name: "Cosmic", desc: "Nebula energy and stardust; scale, connection, and the universe." },
  { n: "9.8", name: "Inferno", desc: "Heated bone, molten cracks, fire, and embers; power, destruction, and rebirth." },
  { n: "9.9", name: "Ice", desc: "Frozen crystal, frost, and cold mist; preservation, resilience, and stillness." },
  { n: "9.10", name: "Void", desc: "Shadow matter and dark energy; absence, possibility, and the unknown." },
];

const RARITIES = [
  { tier: "COMMON", tagline: "The Foundation of Movement", bones: "76", nfts: "760", share: "36.9%",
    ranges: ["107–116: Metacarpals", "117–144: Hand phalanges", "169–178: Metatarsals", "179–206: Foot phalanges"],
    note: "These repeating structures make detailed movement possible and form the mechanical foundation of the hands and feet." },
  { tier: "UNCOMMON", tagline: "Structure in Balance", bones: "55", nfts: "550", share: "26.7%",
    ranges: ["030–053: Cervical, thoracic, and lumbar vertebrae", "055: Coccyx", "091–106: Carpal bones", "155–168: Tarsal bones"],
    note: "These bones support alignment, flexibility, balance, and controlled movement." },
  { tier: "RARE", tagline: "Form Defines Identity", bones: "45", nfts: "450", share: "21.8%",
    ranges: ["002–004: Parietal and occipital bones", "009–021: Facial bones excluding the mandible", "023: Hyoid bone", "024–027: Malleus and incus", "057–080: Ribs"],
    note: "Rare bones shape facial identity, protect vital structures, contribute to hearing, and form the thoracic cage." },
  { tier: "EPIC", tagline: "Strength Builds Potential", bones: "20", nfts: "200", share: "9.7%",
    ranges: ["001: Frontal bone", "056: Sternum", "081–090: Clavicles, scapulae, humeri, radii, and ulnae", "145–146: Left and right hip bones", "149–154: Patellae, tibiae, and fibulae"],
    note: "Epic bones are central to protection, leverage, posture, and large-scale movement." },
  { tier: "LEGENDARY", tagline: "Rare by Nature. Iconic Forever.", bones: "10", nfts: "100", share: "4.9%",
    ranges: ["005–006: Left and right temporal bones", "007: Sphenoid bone", "008: Ethmoid bone", "022: Mandible", "028–029: Left and right stapes", "054: Sacrum", "147–148: Left and right femurs"],
    note: "Legendary bones combine exceptional anatomical importance with distinctive form or symbolism, including the body's largest and smallest bones." },
];

const ROADMAP = [
  { phase: "Phase 1 — Foundation", items: ["Complete anatomy research and master list", "Establish naming conventions and World style guides", "Develop the brand, community channels, and technical requirements"] },
  { phase: "Phase 2 — Production", items: ["Create and review 3D bone models", "Develop materials across ten Worlds", "Build artwork, website, archive, contract, and metadata", "Conduct quality assurance"] },
  { phase: "Phase 3 — Mint", items: ["Publish mint details and complete security review", "Run whitelist and Public Mint", "Complete reveal and marketplace verification"] },
  { phase: "Phase 4 — Experience", items: ["Develop Bone Explorer and educational content", "Introduce profiles, tracking, quizzes, challenges, and events"] },
  { phase: "Phase 5 — Expansion", items: ["Explore gaming, physical collectibles, merchandise, collaborations, and additional interactive experiences"] },
];

const FAQS = [
  { q: "What does an OSTEON NFT represent?", a: "A specific anatomical bone interpreted through one of ten Worlds." },
  { q: "Why 2,060 NFTs?", a: "206 bones × 10 Worlds = 2,060 NFTs." },
  { q: "Is every NFT unique?", a: "Every bone–World combination is intended to be unique." },
  { q: "Which blockchain will be used?", a: "Robinhood Chain. Details announced through official channels." },
  { q: "How will minting work?", a: "1,500 Public Mint, 500 whitelist, and 60 team reserve; price, timing, limits, and mechanics remain TBC." },
  { q: "Where will artwork be stored?", a: "TBC; decentralized storage is under consideration." },
  { q: "What rights do holders receive?", a: "Rights will be defined in the final NFT licence; token ownership does not automatically transfer project IP." },
  { q: "Will more OSTEON NFTs be released?", a: "The original collection is designed to remain fixed at 2,060. Any separate future initiative should be clearly distinguished." },
  { q: "Are roadmap features guaranteed?", a: "No. They depend on feasibility, security, resources, and law." },
  { q: "Is this an investment?", a: "OSTEON NFTs are digital collectibles; profit, liquidity, resale value, and return are not guaranteed." },
];

const GLOSSARY = [
  ["NFT", "A non-fungible token representing a uniquely identifiable blockchain asset."],
  ["Blockchain", "A distributed ledger used to record transactions and ownership."],
  ["Smart contract", "Blockchain code that executes defined rules."],
  ["Metadata", "Structured information describing an NFT and its attributes."],
  ["IPFS", "A decentralized, content-addressed storage protocol."],
  ["Mint", "The process through which an NFT is created or assigned."],
  ["Wallet", "Software or hardware used to manage blockchain assets."],
  ["Rarity", "A classification describing relative scarcity or identity."],
  ["Token ID", "A unique identifier within a smart contract."],
  ["Gas fee", "A network fee for processing a blockchain transaction."],
  ["Reveal", "When final artwork and metadata become visible."],
  ["Whitelist", "Eligible wallets permitted in a defined mint phase."],
];

export default function Whitepaper() {
  const active = useActiveSection();

  useEffect(() => {
    const prev = document.title;
    document.title = "OSTEON Whitepaper — The Human Skeleton, Reimagined";
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) window.setTimeout(() => el.scrollIntoView(), 50);
    } else {
      window.scrollTo(0, 0);
    }
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return (
    <div>
      {/* ==================== HERO ==================== */}
      <section className="relative border-b border-[#1b2234] overflow-hidden">
        <div className="absolute -right-24 -top-16 w-[400px] h-[400px] rounded-full bg-[#00f0ff]/10 blur-[100px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-24">
          <div className="font-mono-cyber text-[11px] uppercase tracking-[0.35em] text-[#00f0ff] mb-4">
            {"// Documentation"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.05]">
            OSTEON<br />
            <span className="text-[#00f0ff]">Official Whitepaper</span>
          </h1>
          <p className="mt-6 font-editorial italic text-xl sm:text-2xl font-light text-slate-300 max-w-2xl leading-relaxed">
            The Architecture of the Human Skeleton, Reimagined.
          </p>
          <div className="mt-6 font-mono-cyber text-[11px] uppercase tracking-[0.3em] text-slate-500">
            Version 1.0 • September 2026
          </div>
          <div className="mt-10 flex items-center gap-8 flex-wrap">
            {[{ v: "206", l: "Bones" }, { v: "10", l: "Worlds" }, { v: "2,060", l: "NFTs" }].map((s) => (
              <div key={s.l} className="flex items-baseline gap-3">
                <div className="font-editorial text-3xl sm:text-4xl font-light text-white">{s.v}</div>
                <div className="coord-label">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== BODY ==================== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
        {/* Mobile TOC */}
        <details className="lg:hidden mb-10 border border-[#1b2234] bg-[#0d1017]/70 clip-terminal">
          <summary className="px-5 py-4 cursor-pointer font-mono-cyber text-[11px] uppercase tracking-[0.3em] text-[#00f0ff] list-none flex items-center justify-between focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00f0ff]">
            <span>[ Table of Contents + ]</span>
          </summary>
          <div className="px-5 pb-6">
            <TocLinks active={active} onNavigate={undefined} />
          </div>
        </details>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Desktop TOC */}
          <aside className="hidden lg:block lg:col-span-3 lg:sticky lg:top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
            <TocLinks active={active} onNavigate={undefined} />
          </aside>

          {/* Content */}
          <main className="lg:col-span-9 min-w-0">
            <article className="space-y-14">
              {/* Disclaimer */}
              <section id="disclaimer" aria-labelledby="disclaimer-heading" className="scroll-mt-28">
                <div className="cyber-corners border border-[#ffb703]/50 bg-[#ffb703]/[0.04] clip-terminal p-6 sm:p-8">
                  <div className="font-mono-cyber text-[11px] uppercase tracking-[0.35em] text-[#ffb703] mb-3">
                    02 · Important Disclaimer — read first
                  </div>
                  <h2 id="disclaimer-heading" className="font-heading text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">
                    Important Disclaimer
                  </h2>
                  <div className="mt-5 space-y-4 text-slate-300 text-[15px] leading-[1.85] max-w-3xl">
                    <p>
                      This whitepaper is provided solely for general informational purposes and presents the current vision, structure, and
                      proposed development of the OSTEON project. Nothing in this document constitutes financial, investment, legal,
                      tax, or professional advice.
                    </p>
                    <p>
                      OSTEON NFTs are digital collectibles. They do not represent securities, shares, equity, ownership in a company,
                      profit-sharing rights, or any guarantee of financial return. OSTEON does not promise or guarantee that the value of
                      any NFT will increase. Market value may rise, fall, or become zero.
                    </p>
                    <p>
                      Utilities, experiences, products, collaborations, integrations, and roadmap features described here represent current
                      intentions. Development may depend on technical feasibility, available resources, security, market conditions,
                      community participation, third-party services, and applicable laws. Features, priorities, and timelines may be
                      modified, postponed, replaced, or discontinued.
                    </p>
                    <p>
                      NFTs, blockchain networks, and digital assets involve risks including market volatility, smart-contract vulnerabilities,
                      cybersecurity threats, regulatory change, network disruption, third-party dependencies, and loss of wallet credentials
                      or private keys.
                    </p>
                    <p>
                      Participants should conduct independent research, seek qualified advice where appropriate, and ensure compliance
                      with laws, taxes, and eligibility requirements in their jurisdiction. By participating, they acknowledge and accept the
                      risks associated with NFTs, blockchain technology, digital assets, and digital ownership.
                    </p>
                  </div>
                </div>
              </section>

              <Section id="executive-summary" index="03 · Executive Summary" title="Executive Summary">
                <p>
                  OSTEON is a digital anatomical archive that transforms the architecture of the human skeleton into a structured
                  collection of digital artwork and NFTs. Instead of beginning with random characters and adding a narrative later,
                  OSTEON starts with something real, measurable, and universally recognizable: the adult human skeleton.
                </p>
                <p>
                  Every OSTEON NFT represents a defined anatomical bone and retains a connection to its real form, position,
                  function, and identity. The same system is interpreted through ten Worlds—Origin, Obsidian, Gold, Crystal, Cyber,
                  Bio, Cosmic, Inferno, Ice, and Void—each with distinctive materials, colours, lighting, environments, and visual
                  effects.
                </p>
                <Formula>206 Bones × 10 Worlds = 2,060 NFTs</Formula>
                <p>
                  The planned allocation is 1,500 Public Mint NFTs, 500 whitelist NFTs, and 60 team-reserve NFTs. Each collectible
                  may include a Token ID, Bone ID, anatomical name, skeletal region, side, World, material, rarity, visual traits, and
                  media location.
                </p>
                <p>
                  OSTEON operates at the intersection of <strong className="text-slate-100">Science × Art × Gaming × Community × Digital Ownership</strong>. Collectors
                  may pursue a meaningful bone, anatomical region, complete skeletal category, preferred World, rarity, or the same
                  bone across multiple Worlds.
                </p>
                <p>
                  Potential future experiences include detailed anatomy, interactive 3D viewing, quizzes, collecting challenges,
                  community quests, leaderboards, gaming, physical artwork, merchandise, and selected collaborations. These
                  experiences may expand, but the 206-bone framework, ten Worlds, and fixed supply remain the foundation.
                </p>
              </Section>

              <Section id="vision-mission" index="04 · Vision and Mission" title="Vision and Mission">
                <Sub>Vision</Sub>
                <p>
                  OSTEON seeks to create a permanent digital anatomical archive by bringing together science, anatomy, art,
                  technology, and digital ownership. It preserves the recognizable architecture of the human skeleton while presenting
                  it through imaginative visual Worlds and interactive experiences.
                </p>
                <p className="font-editorial italic text-lg text-[#00f0ff]">The anatomy remains constant. The experience evolves.</p>
                <Sub>Mission</Sub>
                <Bullets items={[
                  "Make human anatomy visually engaging without losing anatomical recognizability.",
                  "Give every NFT an identifiable bone, position, region, World, material, and rarity.",
                  "Transform collecting into an educational and interactive experience.",
                  "Maintain the fixed formula of 206 bones × 10 Worlds = 2,060 NFTs.",
                ]} />
              </Section>

              <Section id="problem" index="05 · The Problem" title="The Problem">
                <p>
                  Many NFT collections rely on randomly generated characters, interchangeable cosmetic traits, or short-term market
                  trends. They may offer visual variation without a meaningful reason for why each asset exists or how it belongs to
                  the whole.
                </p>
                <Bullets items={[
                  "Randomness without a meaningful underlying structure.",
                  "Limited individual identity beyond accessories or rarity scores.",
                  "Supply figures with no direct relationship to the concept.",
                  "Collector engagement that declines after mint and reveal.",
                  "A limited connection between educational value and collectible art.",
                  "Uncertainty about future releases and dilution of the original collection.",
                ]} />
                <p>
                  A stronger system should explain what each asset represents, why it exists, how it differs, where it belongs, and
                  what gives it meaning beyond market value.
                </p>
              </Section>

              <Section id="osteon-solution" index="06 · The OSTEON Solution" title="The OSTEON Solution">
                <p>
                  OSTEON builds its collection around the organized anatomy of the adult human skeleton. Each NFT represents one
                  specific bone in one specific World, giving it a recognizable identity, defined anatomical position, structured
                  metadata, and permanent place in the archive.
                </p>
                <p>
                  Each bone is interpreted once in every World. No two bone–World combinations are intended to be identical.
                  Educational information, interactive experiences, and community participation extend the experience while the
                  original anatomical framework and supply remain fixed.
                </p>
              </Section>

              <Section id="scientific-foundation" index="07 · Scientific and Anatomical Foundation" title="Scientific and Anatomical Foundation">
                <Sub>The 206-Bone Framework</Sub>
                <Table
                  caption="206-bone framework by skeletal division"
                  headers={["Division", "Bones"]}
                  rows={[["Axial skeleton", "80"], ["Appendicular skeleton", "126"], ["Total", "206"]]}
                />
                <p>
                  The axial skeleton includes the skull, vertebral column, ribs, and sternum. The appendicular skeleton includes the
                  shoulder girdles, upper limbs, pelvic girdle, and lower limbs.
                </p>
                <Sub>Organization and Naming</Sub>
                <p>
                  The archive organizes bones by recognized regions: skull and facial bones, auditory ossicles, hyoid, vertebral
                  column, thoracic cage, pectoral girdle, upper limbs, pelvic girdle, and lower limbs. Records may include
                  standardized and common names, category, region, side, and anatomical position.
                </p>
                <p>
                  Paired left and right bones are separate positions. Midline bones are identified accordingly. Carpals, tarsals,
                  phalanges, ribs, vertebrae, and auditory ossicles retain individual identities.
                </p>
                <Sub>Variation and Verification</Sub>
                <p>
                  Individual anatomy can vary. OSTEON uses the standard adult 206-bone model for consistency. Before final
                  production, the master list, naming, orientation, classification, morphology, and visuals should be reviewed by a
                  qualified anatomy consultant or relevant medical professional. OSTEON is an art and collectible project, not medical
                  advice.
                </p>
              </Section>

              <Section id="collection-architecture" index="08 · Collection Architecture" title="Collection Architecture">
                <Formula>206 Bones × 10 Worlds = 2,060 NFTs</Formula>
                <p>
                  Every anatomical position appears once in each World. Each bone–World combination represents one unique NFT.
                  Exact duplicate combinations are excluded from the original collection.
                </p>
                <p>
                  Each bone receives a unique global Bone ID from <strong className="text-slate-100">OST-B001</strong> through <strong className="text-slate-100">OST-B206</strong>. A World code may form a
                  complete identifier such as <strong className="text-slate-100">OST-B001-ORG</strong>. Token IDs provide separate blockchain references.
                </p>
                <p>
                  Collectors will verify assets through the official contract, Token ID, Bone ID, anatomical name, World, metadata,
                  marketplace verification, and OSTEON archive. Only tokens linked to the verified official contract belong to the
                  original collection.
                </p>
              </Section>

              <Section id="ten-worlds" index="09 · The Ten Worlds" title="The Ten Worlds">
                <div className="grid sm:grid-cols-2 gap-4 !mt-8">
                  {WORLDS.map((w) => (
                    <div key={w.name} className="cyber-corners p-6 bg-[#0d1017]/60 border border-[#1b2234] clip-terminal hover:border-[#00f0ff]/50 h-full">
                      <div className="coord-label mb-3">{w.n}</div>
                      <div className="font-editorial text-2xl font-light text-white">{w.name}</div>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{w.desc}</p>
                    </div>
                  ))}
                </div>
                <p className="!mt-6 text-slate-400 italic font-editorial">
                  Across every World, materials and effects must enhance rather than conceal anatomical identity.
                </p>
              </Section>

              <Section id="art-creation" index="10 · Art Creation Process" title="Art Creation Process">
                <p>
                  Every asset begins with anatomical references. The intended workflow includes research, base 3D modelling, digital
                  sculpting, morphology review, World-specific materials, texturing, lighting, rendering, post-production, and quality
                  control.
                </p>
                <p>
                  Human artists define anatomy, composition, World style guides, materials, lighting, visual effects, and final
                  selection. AI-assisted tools may support concept exploration or selected production tasks, but outputs require
                  human review, refinement, and anatomical inspection.
                </p>
                <Bullets items={[
                  "Verify anatomical recognizability and correct orientation.",
                  "Maintain World consistency and professional visual quality.",
                  "Remove duplication, distortion, and malformed anatomy.",
                  "Confirm metadata and technical rendering accuracy.",
                ]} />
                <p>
                  Final assets may include high-resolution images, animation, video, or 3D models. Media specifications remain TBC.
                </p>
              </Section>

              <Section id="metadata" index="11 · NFT Metadata Structure" title="NFT Metadata Structure">
                <Bullets items={[
                  "Token ID and Bone ID",
                  "Anatomical and common name",
                  "Category, region, and side",
                  "World, material, rarity, and visual traits",
                  "Description and media locations",
                  "3D asset location where available",
                  "Metadata version",
                ]} />
                <div className="overflow-x-auto">
                  <pre className="bg-[#07080c] border border-[#1b2234] p-5 font-mono-cyber text-[13px] leading-relaxed text-[#00ff66] overflow-x-auto"><code>{`{
  "name": "OSTEON #0001",
  "bone_id": "001",
  "anatomical_name": "Frontal Bone",
  "skeletal_region": "Skull",
  "world": "Origin",
  "rarity": "Epic",
  "image": "ipfs://TBC"
}`}</code></pre>
                </div>
                <p>
                  The final schema depends on the selected blockchain, token standard, storage system, and marketplace
                  requirements.
                </p>
              </Section>

              <Section id="rarity-system" index="12 · Rarity System" title="Rarity System">
                <p className="font-heading text-lg font-bold uppercase tracking-wide text-[#00f0ff]">
                  Final locked rarity model
                </p>
                <p>
                  OSTEON uses a predetermined, anatomy-based rarity system. Rarity is attached to the identity
                  of the bone—not generated randomly at mint. Every bone keeps the same rarity across all ten
                  Worlds.
                </p>
                <p>
                  For example, the left femur remains Legendary in Origin, Obsidian, Gold, Crystal, Cyber, Bio, Cosmic, Inferno, Ice,
                  and Void. Its artistic treatment changes, but its anatomical identity and rarity remain permanent.
                </p>
                <Table
                  caption="Locked rarity distribution"
                  headers={["Rarity", "Bones / World", "NFTs / 10 Worlds", "Share"]}
                  rows={[
                    ["COMMON", "76", "760", "36.9%"],
                    ["UNCOMMON", "55", "550", "26.7%"],
                    ["RARE", "45", "450", "21.8%"],
                    ["EPIC", "20", "200", "9.7%"],
                    ["LEGENDARY", "10", "100", "4.9%"],
                    ["TOTAL", "206", "2,060", "100%"],
                  ]}
                />
                <div className="space-y-8 !mt-10">
                  {RARITIES.map((r) => (
                    <div key={r.tier} className="cyber-corners border border-[#1b2234] bg-[#0d1017]/60 clip-terminal p-6 sm:p-8">
                      <div className="font-heading text-xl font-bold uppercase">
                        <span className="text-[#00f0ff]">{r.tier}</span>
                        <span className="text-slate-500 font-mono-cyber text-xs tracking-widest"> — {r.bones} bones per world</span>
                      </div>
                      <div className="mt-1 font-editorial italic text-slate-300">{r.tagline}</div>
                      <ul className="mt-4 space-y-1.5 font-mono-cyber text-[13px] text-slate-400">
                        {r.ranges.map((range) => (
                          <li key={range}>• {range}</li>
                        ))}
                      </ul>
                      <div className="mt-4 font-mono-cyber text-[11px] uppercase tracking-widest text-slate-500">
                        Across ten Worlds: {r.nfts} NFTs • Collection share: {r.share}
                      </div>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{r.note}</p>
                    </div>
                  ))}
                </div>
                <Sub>Permanent Assignment and Verification</Sub>
                <p>
                  Every rarity assignment is locked to a unique global Bone ID from <strong className="text-slate-100">001 to 206</strong>. Rarity will be written into NFT
                  metadata and connected to the bone's anatomical name, skeletal region, side, and World. Once final metadata is
                  frozen, rarity will not be upgraded, downgraded, rerolled, or reassigned.
                </p>
                <p className="font-heading font-bold text-slate-100">206 Bones. Five Rarities. Ten Worlds. One Fixed System.</p>
                <p className="text-sm text-slate-500">
                  The whitepaper's visual rarity reference (3rd Edition artwork) accompanies this structure.
                  The written global Bone ID ranges above are authoritative.
                </p>
              </Section>

              <Section id="blockchain" index="13 · Blockchain and Technical Infrastructure" title="Blockchain and Technical Infrastructure">
                <p>
                  OSTEON has selected Robinhood Chain as its blockchain network for the collection, with the
                  ERC-721 token standard (planned) for unique digital collectibles.
                </p>
                <p>
                  Selection will consider security, transaction costs, environmental efficiency, wallet support, marketplace
                  compatibility, developer tooling, storage integration, and long-term reliability.
                </p>
                <p>
                  <strong className="text-slate-100">Official channels and verification</strong> · Website: osteonnft.xyz · X: @OsteonNFT.
                  Contract details are official only when published through these verified channels.
                </p>
                <p>
                  If an EVM-compatible network is selected, OSTEON may use ERC-721. The smart contract should define
                  maximum supply, minting permissions, allocations, wallet limits, metadata functions, transfers, administration,
                  royalties where supported, and supply-protection controls.
                </p>
                <p>
                  Before minting, OSTEON will disclose the selected network, official contract address, token standard, mint mechanism,
                  ownership controls, upgradeability status, audit status, verified source-code location, and permanent verification
                  guidance.
                </p>
                <p>
                  OSTEON has selected Robinhood Chain as its intended blockchain network for the collection. The network is intended
                  to support secure NFT ownership, minting, transfers, and marketplace integration.
                </p>
                <Formula>Network: Robinhood Chain&nbsp;&nbsp;|&nbsp;&nbsp;Token standard: ERC-721 (planned)</Formula>
                <p>
                  The smart contract should define the maximum supply of 2,060 NFTs, minting permissions, the 1,500 Public / 500 Whitelist / 60
                  Team allocation, wallet limits, metadata functions, transfers, administration, royalties where supported, and supply-protection
                  controls.
                </p>
                <p>
                  Before minting, OSTEON will publish the official contract address, deployment status, mint mechanism, ownership controls, upgradeability
                  status, audit status, verified source code, and permanent verification guidance through its official channels.
                </p>
              </Section>

              <Section id="storage" index="14 · Storage and Permanence" title="Storage and Permanence">
                <p>
                  The final storage architecture remains TBC. Decentralized systems such as IPFS or Arweave are under
                  consideration so essential artwork and metadata can remain accessible beyond a single website.
                </p>
                <Bullets items={[
                  "Address artwork, metadata, animation, 3D assets, redundancy, and persistence.",
                  "Complete artwork, metadata, anatomy, file, and link validation before freezing.",
                  "Disclose any centralized dependency clearly.",
                ]} />
              </Section>

              <Section id="mint-structure" index="15 · Mint Structure" title="Mint Structure">
                <Table
                  caption="Mint allocation"
                  headers={["Allocation", "NFTs", "Share"]}
                  rows={[
                    ["Public Mint", "1,500", "72.8%"],
                    ["Whitelist", "500", "24.3%"],
                    ["Team Reserve", "60", "2.9%"],
                    ["TOTAL", "2,060", "100%"],
                  ]}
                />
                <p>
                  The following remain TBC: price, currency, dates, phases, wallet limits, whitelist eligibility, random or selectable
                  assignment, reveal, anti-bot controls, treatment of unsold NFTs, refunds where applicable, team-token restrictions,
                  and contract withdrawal controls.
                </p>
              </Section>

              <Section id="revenue-allocation" index="16 · Revenue and Fund Allocation" title="Revenue and Fund Allocation">
                <p>
                  Primary-mint revenue is intended to support anatomy research, 3D and art production, animation, contracts,
                  security, website development, storage, community operations, marketing, legal compliance, future products, and
                  reserves. Exact percentages will be published only after budgets are finalized. Material changes should be
                  communicated transparently.
                </p>
              </Section>

              <Section id="utility" index="17 · Utility and Collector Benefits" title="Utility and Collector Benefits">
                <Bullets items={[
                  "Detailed bone information and anatomy-learning modules",
                  "Interactive 3D viewing, holder profiles, and collection tracking",
                  "Quizzes, educational challenges, quests, and events",
                  "World-based collecting experiences",
                  "Physical artwork and merchandise opportunities",
                  "Gaming integrations and community feedback",
                  "Commercial rights only where formally granted",
                ]} />
                <p>
                  Every utility should be classified as available, under development, planned, or experimental. Features may have
                  eligibility, technical, regional, cost, or availability limits. Ownership does not guarantee every proposed utility.
                </p>
              </Section>

              <Section id="community" index="18 · Community Ecosystem" title="Community Ecosystem">
                <p>
                  The community may include official social channels, anatomy challenges, quests, World-based groups,
                  leaderboards, referrals, showcases, feedback sessions, and holder events. Operations should promote respectful
                  participation, accurate information, scam prevention, fair moderation, transparent rewards, and privacy. Important
                  links and contract details should be trusted only through verified OSTEON channels.
                </p>
              </Section>

              <Section id="ip-rights" index="19 · Intellectual Property Rights" title="Intellectual Property Rights">
                <p>
                  Purchasing an OSTEON NFT transfers ownership of the token, not automatic ownership of the OSTEON brand,
                  logo, source files, 3D models, or wider collection. The final licence must define display, social, commercial,
                  merchandise, modification, animation, and 3D rights.
                </p>
                <p>
                  Where rights depend on token ownership, they should transfer or terminate upon sale. Original artwork, branding,
                  writing, animations, and models may be protected even where anatomical facts and standard names are not. The
                  final licence requires professional legal review before mint.
                </p>
              </Section>

              <Section id="royalties" index="20 · Royalty Structure" title="Royalty Structure">
                <p>
                  OSTEON may apply a creator royalty to eligible secondary sales; the percentage is TBC. Revenue may support
                  development, community, education, infrastructure, security, storage, and legal expenses. Marketplace enforcement
                  varies, so OSTEON cannot guarantee royalty collection or secondary-market activity.
                </p>
              </Section>

              <Section id="roadmap" index="21 · Roadmap" title="Roadmap">
                <div className="space-y-8 !mt-8">
                  {ROADMAP.map((stage) => (
                    <div key={stage.phase} className="border-l border-[#00f0ff]/40 pl-6 sm:pl-8 relative">
                      <span aria-hidden="true" className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rotate-45 bg-[#0d1017] border border-[#00f0ff]" />
                      <div className="font-heading text-lg font-bold uppercase text-[#00f0ff]">{stage.phase}</div>
                      <ul className="mt-3 space-y-2">
                        {stage.items.map((item) => (
                          <li key={item} className="flex gap-3 text-[15px]">
                            <span aria-hidden="true" className="text-slate-500 flex-shrink-0">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="!mt-8 text-slate-400 italic font-editorial">
                  Dates will be published only after dependencies and resources have been assessed realistically.
                </p>
              </Section>

              <Section id="team" index="22 · Team and Advisors" title="Team and Advisors">
                <p>
                  Potential roles include founder and creative director, project manager, anatomy consultant, 3D and texture artists,
                  VFX artists, website and smart-contract developers, community manager, legal advisor, and security auditor. Each
                  disclosed profile should include identity, role, experience, responsibilities, and professional links. Final profiles
                  remain TBC.
                </p>
              </Section>

              <Section id="governance" index="23 · Governance and Decision-Making" title="Governance and Decision-Making">
                <p>
                  Community feedback may influence experiences, events, merchandise, collaborations, and platform improvements.
                  This does not automatically establish a DAO. The core team may retain responsibility for law, security, treasury, IP,
                  contracts, anatomy, brand, and operations.
                </p>
                <p>
                  If voting is introduced, OSTEON will define eligibility, power, duration, quorum, advisory or binding status, conflicts,
                  and manipulation safeguards. No DAO rights will be promised without design and legal review.
                </p>
              </Section>

              <Section id="security" index="24 · Security" title="Security">
                <Bullets items={[
                  "Smart-contract testing and independent audit where feasible",
                  "Verified source code and restricted administrative controls",
                  "Multisignature treasury and secure deployment",
                  "Website, domain, backup, and incident-response protection",
                  "Official-link verification, phishing warnings, and wallet education",
                ]} />
                <p>
                  OSTEON representatives will never request seed phrases, private keys, or recovery phrases. No system is
                  risk-free; audit and security status must be disclosed accurately.
                </p>
              </Section>

              <Section id="legal" index="25 · Legal and Regulatory Considerations" title="Legal and Regulatory Considerations">
                <p>
                  OSTEON NFTs are intended as digital collectibles and access points to defined experiences. They do not represent
                  equity, shares, guaranteed income, profit-sharing, debt, or guaranteed return.
                </p>
                <p>
                  The final framework should include terms, privacy, NFT licence, age and jurisdiction rules, sanctions where
                  applicable, consumer protection, tax responsibility, data protection, IP, disputes, and refunds. Regulation evolves,
                  and access or functionality may change for compliance. Qualified legal review is required before launch.
                </p>
              </Section>

              <Section id="risks" index="26 · Risks" title="Risks">
                <Bullets items={[
                  "Market risk: value may be volatile or fall to zero.",
                  "Blockchain risk: networks may face fees, congestion, change, interruption, or failure.",
                  "Smart-contract risk: code may contain vulnerabilities despite review.",
                  "Cybersecurity risk: phishing, impersonation, fraud, and wallet drains.",
                  "Regulatory risk: laws may affect access, transferability, tax, or operations.",
                  "Marketplace risk: third-party platforms may change policies, fees, access, or royalties.",
                  "Development and adoption risk: features may be delayed, changed, cancelled, or underused.",
                  "IP risk: disputes may concern art, branding, licences, or third-party content.",
                  "Wallet risk: lost credentials may permanently prevent access.",
                ]} />
              </Section>

              <Section id="sustainability" index="27 · Sustainability" title="Sustainability">
                <p>
                  OSTEON will consider network energy use, transaction efficiency, storage durability, infrastructure reliability, hosting
                  and maintenance costs, treasury sustainability, and operations. Where practical, the project should favour efficient
                  networks and durable decentralized storage. Responsible budgeting should reduce dependence on continuous NFT
                  releases.
                </p>
              </Section>

              <Section id="success-metrics" index="28 · Success Metrics" title="Success Metrics">
                <Bullets items={[
                  "Completion and review of the 206-bone master list",
                  "Asset completion across ten Worlds",
                  "Mint completion, unique-holder distribution, and retention",
                  "Bone Explorer, education, 3D viewer, quiz, and community engagement",
                  "Product milestones, uptime, and security response",
                  "Collaborations delivered and collector feedback",
                ]} />
                <p>
                  Floor price will not be the sole measure of success. The broader objective is a recognizable, reliable, engaging
                  anatomical archive with lasting artistic and educational relevance.
                </p>
              </Section>

              <Section id="faq" index="29 · Frequently Asked Questions" title="Frequently Asked Questions">
                <div className="space-y-7">
                  {FAQS.map((item) => (
                    <div key={item.q}>
                      <h3 className="font-heading text-base sm:text-lg font-bold text-[#00f0ff] normal-case tracking-normal">
                        {item.q}
                      </h3>
                      <p className="mt-1.5">{item.a}</p>
                    </div>
                  ))}
                </div>
              </Section>

              <Section id="glossary" index="30 · Glossary and References" title="Glossary and References">
                <dl className="space-y-3">
                  {GLOSSARY.map(([term, def]) => (
                    <div key={term} className="flex flex-col sm:flex-row sm:gap-3">
                      <dt className="font-bold text-slate-100 flex-shrink-0 sm:min-w-[140px]">{term}:</dt>
                      <dd className="text-slate-400">{def}</dd>
                    </div>
                  ))}
                </dl>
                <Sub>References to Finalize</Sub>
                <Bullets items={[
                  "Standard anatomy textbooks and recognized anatomical terminology",
                  "Scientific sources supporting the 206-bone framework",
                  "Selected blockchain, wallet, token-standard, and royalty documentation",
                  "Verified contract source code and audit where available",
                  "Selected decentralized-storage documentation",
                  "Professional anatomical and legal review",
                ]} />
                <p>
                  The final bibliography will be completed after the network, storage system, anatomical sources, and professional
                  reviewers are confirmed.
                </p>
              </Section>

              {/* Closing */}
              <section aria-label="Closing" className="border-t border-[#1b2234] pt-14 text-center">
                <div className="font-mono-cyber text-[11px] uppercase tracking-[0.35em] text-[#00f0ff] mb-6">
                  Welcome to Osteon
                </div>
                <p className="font-editorial text-2xl sm:text-3xl font-light text-white leading-[1.5]">
                  Every piece belongs to a larger structure.<br />
                  Every number has meaning.<br />
                  Every bone has an <span className="italic text-[#00f0ff]">anatomical identity</span>.<br />
                  Every World offers a new interpretation.
                </p>
                <p className="mt-8 font-heading text-lg font-bold tracking-wide">
                  206 Bones. 10 Worlds. 2,060 NFTs.
                </p>
                <p className="mt-2 font-editorial italic text-slate-400">
                  One evolving experience. One permanent archive.
                </p>
                <p className="mt-10 font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-slate-500">
                  OSTEON Official Whitepaper · Version 1.0 · September 2026 · © 2026 OSTEON
                </p>
              </section>
            </article>
          </main>
        </div>
      </div>
    </div>
  );
}
